import { Injectable } from '@angular/core';
import { IItemsListAction2, IItemsListPublicMethods } from '@fi-shared/fi-items-list/fi-items-list';
import { IForm, IFormReport } from '@fi-shared/fi-form-shared/fi-form-shared';
import { ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiItemsEditHelperService } from '@fi-shared/fi-shared-services/fi-items-edit-helper.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { IItemWithDeleted, TResultPromise, TResultPromiseData } from '@fi-shared/fi-shared-interfaces';
import { IDialogConfirmDeleteData } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete-data';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';
import { FiFormSharedService } from '@fi-shared/fi-form-shared/fi-form-shared.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { fiTextToHtml } from '@fi-shared/utils/fi-text-to-html';

interface IGetFormActionsArgs {
    // todo implement generic interface?
    afterDeleteFormAnswersFn?: (form: IForm) => void;
    reloadList?: () => void;
}

@Injectable()
export class FiFormActionsService {
    constructor(
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiStateService: FiStateService,
        private appTextService: AppTextService,
        private formService: FiFormService,
        private formSharedService: FiFormSharedService,
        private formHelperService: FiFormHelperService,
        private fiLoadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private snackBarService: FiSnackBarService,
        private itemsDeleteHelperService: FiItemsDeleteHelperService,
        private itemsEditHelperService: FiItemsEditHelperService,
    ) {}

    // region form edit/view report
    getViewReportAction(): IItemsListAction2<IForm | IFormReport> {
        return {
            title: this.appTextService.get('form.view_report'),
            onClick: (form) => this.fiStateService.go(FiStateNames.Form.Report.View, { id: form.Id }),
            disabled: (data) => !data.HasReportsAccess,
        };
    }

    getEditFormAction(): IItemsListAction2<IForm | IFormReport> {
        return {
            title: this.appTextService.get('form.edit_form'),
            disabled: (data) => !this.formHelperService.isEditAllowedByModuleRights() || !data.HasEditAccess,
            onClick: (form) => {
                this.itemsEditHelperService.showEditRestoreConfirm(
                    form.Name,
                    form.IsDeleted,
                    () => this.fiStateService.go(FiStateNames.Form.Edit, { id: form.Id }),
                    () => this.formService.restoreForms([form.Id]),
                );
            },
        };
    }
    // endregion

    /// region form delete answers
    doDeleteFormAnswers(forms: IForm[] | IFormReport[], answersCout: number): TResultPromise {
        const promise = new Promise<TResultPromiseData>((resolve, reject) => {
            const formData =
                forms.length === 1
                    ? forms[0].Name
                    : this.appTextService.get('form.x_forms').replace('{0}', forms.length.toString());
            const confirmMessage = this.appTextService.get('checklist.confirm_delete_responses_text', {
                '{0}': fiTextToHtml(formData),
                '{1}': answersCout.toString(),
            });
            const formIds = forms.map((formReport) => formReport.Id);
            this.formDeleteResponsesConfirm(formIds, confirmMessage).then((r) => resolve(r), reject);
        });

        return promise;
    }

    private formDeleteResponsesConfirm(formIds: number[], confirmMessage: string): TResultPromise {
        return new Promise<TResultPromiseData>((resolve, reject) => {
            const dialogContentData: IDialogConfirmDeleteData = {
                step1: {
                    message: confirmMessage,
                    buttonText: this.appTextService.get('common.confirm'),
                    confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                },
                step2: {
                    message: 'checklist.confirm_delete_responses_text_2',
                    buttonText: this.appTextService.get('checklist.delete_responses').toUpperCase(),
                },
            };

            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('common.warning'),
                    contentComponentRef: FiDialogConfirmDeleteComponent,
                    contentComponentData: dialogContentData,
                    config: {
                        maxWidth: '400px',
                    },
                })
                .onYesButtonClick(() => {
                    this.formSharedService.deleteFormAnswers(formIds).then(() => {
                        this.snackBarService.showSnackbar({
                            textMessage: this.appTextService.get('common.responses_deleted'),
                            buttonText: this.appTextService.get('common.dismiss'),
                        });
                        resolve({ result: true });
                    }, reject);
                })
                .onNoButtonClick(() => resolve({ result: false }));
        });
    }

    // endregion

    /// region form delete
    doDeleteForms<T extends IItemWithDeleted>(
        itemsToDelete: T[],
        itemsListMethods: IItemsListPublicMethods<T>,
    ): TResultPromise {
        const promise = new Promise<TResultPromiseData>((resolve, reject) => {
            this.formDeleteConfirm(itemsToDelete).then((r) => {
                if (r.result) {
                    const itemsToDeleteIds = itemsToDelete.map((item) => item.Id);
                    // todo process backend error?
                    const isDeletedFilter = itemsListMethods.getFilterType() === SideListOptionType.Deleted;
                    this.formService.deleteForms(itemsToDeleteIds).then(() => {
                        itemsListMethods.removeData(itemsToDelete);

                        this.itemsDeleteHelperService.showDeletedObjectSnackBar(
                            ObjectTypes.Form,
                            itemsToDelete,
                            isDeletedFilter,
                            () => this.formService.restoreForms(itemsToDeleteIds),
                            () => itemsListMethods.reloadData(),
                        );

                        resolve({ result: true });
                    }, reject);
                } else {
                    resolve(r);
                }
            });
        });
        return promise;
    }

    private formDeleteConfirm<T extends { IsDeleted?: boolean }>(itemsToDelete: T[]): TResultPromise {
        /*
                // https://chainformation.atlassian.net/browse/FI-4683
                // exlude answer set rows from items to delete
                const itemsToDelete = selectedItems.filter((item: any) => !item.FormId);
        */
        return new Promise<TResultPromiseData>((resolve) => {
            if (itemsToDelete[0].IsDeleted) {
                this.fiDialogsService
                    .confirm({
                        headerText: this.appTextService.get('form.delete_permanently_multiple_confirm_header'),
                        messageText: this.appTextService.get('form.delete_permanently_multiple_confirm'),
                        yesButtonText: this.appTextService.get('common.yes_delete'),
                        config: {
                            maxWidth: '400px',
                        },
                    })
                    .onYesButtonClick(() => resolve({ result: true }))
                    .onNoButtonClick(() => resolve({ result: false }));
            } else {
                const dialogContentData: IDialogConfirmDeleteData = {
                    step1: {
                        message:
                            itemsToDelete.length > 1
                                ? 'form.form_delete_multiple_message'
                                : 'form.form_delete_formlist_text_1',
                        buttonText: this.appTextService.get('common.confirm'),
                        confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                    },
                };

                this.fiDialogsService
                    .custom({
                        headerText:
                            itemsToDelete.length > 1
                                ? this.appTextService.get('form.form_delete_multiple_header')
                                : this.appTextService.get('common.warning'),
                        contentComponentRef: FiDialogConfirmDeleteComponent,
                        contentComponentData: dialogContentData,
                        config: {
                            maxWidth: '400px',
                        },
                    })
                    .onYesButtonClick(() => resolve({ result: true }))
                    .onNoButtonClick(() => resolve({ result: false }));
            }
        });
    }
    /// endregion

    /// region form restore
    doRestoreForms<T extends IItemWithDeleted>(
        itemsToRestore: T[],
        itemsListMethods: IItemsListPublicMethods<T>,
    ): TResultPromise {
        const promise = new Promise<TResultPromiseData>((resolve, reject) => {
            this.itemsDeleteHelperService.restoreItems({
                itemsToRestore: itemsToRestore,
                objectType: ObjectTypes.Form,
                restoreFn: () => {
                    return this.formService.restoreForms(itemsToRestore.map((item) => item.Id)).then(() => {
                        resolve({ result: true });
                    }, reject);
                },
                afterRestoreFn: (restoredItems) => itemsListMethods.removeData(restoredItems),
            });
        });
        return promise;
    }
    /// endregion
}
